import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Zap, Droplets, Wrench, Shield, Building2, CheckCircle2,
  Phone, Mail, ChevronDown, ChevronRight,
  HardHat, Plug, PipetteIcon, Hammer, FileText, Download,
  Star, Clock, BadgeCheck, Users, MapPin, AlertTriangle,
  Beaker, Activity, ArrowRight, Award, Briefcase, ClipboardCheck, Gavel, DollarSign,
  X, GraduationCap, Heart, Landmark, Quote
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import SiteHeader from "@/components/SiteHeader";
import BackToHome from "@/components/BackToHome";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const GC_BROCHURE_URL = `${BACKEND_URL}/api/downloads/gc-brochure`;

const LOGO = "/logos/sterilemate-white.png?v=4";
const LOGO_LOCAL = "/images/sterilemate-logo-white.png";

const IMAGES = {
  hero: `${BACKEND_URL}/api/static/gc-tiles/hero_or_clinic_split.png`,
  electrical: "https://images.unsplash.com/photo-1758101755915-462eddc23f57?w=800&q=80",
  plumbing: "https://images.unsplash.com/photo-1602336585318-0801ac2f37b9?w=800&q=80",
  construction: "https://images.unsplash.com/photo-1768321916340-b05845f26f9e?w=800&q=80",
  hospital: "https://images.unsplash.com/photo-1772299121503-cd62a57e3a26?w=800&q=80",
  buildout: "https://images.unsplash.com/photo-1768321914670-80db17b4669b?w=800&q=80",
  hallway: "https://images.unsplash.com/photo-1638926216050-dc056b89540c?w=800&q=80",
  waterSystem: "https://customer-assets.emergentagent.com/job_6b00dc0f-3fbc-4d33-80da-2aa733a89314/artifacts/sg8wbqak_dialysis%20Water%20Purifcation%20system.png",
  waterRoom: "https://customer-assets.emergentagent.com/job_6b00dc0f-3fbc-4d33-80da-2aa733a89314/artifacts/qyew7d2h_dialysis%20Water%20Purifcation%20room.png",
  utilityWater: "https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=800&q=80",
};

// ================================================================
// Start-a-Project Lead Capture — posts to Mongo via /api/leads/coming-soon
// (matches the "Contact Card Instead of Mailto" ask; email fallback kept in CTA)
// ================================================================
const StartProjectForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    projectType: "General Inquiry",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errMsg, setErrMsg] = useState("");

  const projectTypes = [
    "General Inquiry",
    "Isolation / Patient Rooms",
    "Operating / Procedure Rooms",
    "Ambulatory Surgery Center",
    "Sterile Processing (SPD)",
    "Compounding Pharmacy (USP 797/800)",
    "Water & MEP Infrastructure",
    "Behavioral Health / NICU",
    "Emergency Break-Fix",
    "Facility Upgrade / Renovation",
    "Other",
  ];

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrMsg("");
    try {
      const payload = {
        product_id: "gc-start-project",
        product_name: `GC Lead — ${form.projectType}`,
        email: form.email.trim(),
        name: form.name.trim() || null,
        company: form.company.trim() || null,
        message: form.message.trim() || null,
      };
      const res = await fetch(`${BACKEND_URL}/api/leads/coming-soon`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.detail || "Submission failed");
      }
      setStatus("success");
      setForm({ name: "", email: "", company: "", projectType: "General Inquiry", message: "" });
    } catch (err) {
      setStatus("error");
      setErrMsg(err.message || "Something went wrong. Please email george.broughton@sterilemate.com.");
    }
  };

  return (
    <section id="gc-start-project" className="py-16 scroll-mt-24" data-testid="gc-start-project-form-section">
      <div className="grid md:grid-cols-5 gap-8 items-stretch bg-gradient-to-br from-slate-50 via-white to-cyan-50 border-2 border-cyan-200 rounded-2xl p-8 md:p-12 shadow-xl">
        {/* Left context column */}
        <div className="md:col-span-2 flex flex-col justify-center">
          <p className="text-[11px] font-bold uppercase tracking-widest text-cyan-700 mb-2">
            Start a Project
          </p>
          <h2 className="text-3xl md:text-4xl font-black text-slate-900 leading-tight mb-4">
            Tell us about your build.
          </h2>
          <p className="text-slate-600 mb-6 leading-relaxed">
            Capital projects, room retrofits, emergency break-fix, or a new
            ambulatory surgery center — send a note and a senior team member will
            follow up within one business day.
          </p>
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-3 text-slate-700">
              <Phone className="w-4 h-4 text-cyan-700" />
              <a href="tel:2814563458" className="hover:text-cyan-700">281.456.3458</a>
            </div>
            <div className="flex items-center gap-3 text-slate-700">
              <Mail className="w-4 h-4 text-cyan-700" />
              <a href="mailto:george.broughton@sterilemate.com" className="hover:text-cyan-700">
                george.broughton@sterilemate.com
              </a>
            </div>
            <div className="flex items-center gap-3 text-slate-700">
              <MapPin className="w-4 h-4 text-cyan-700" />
              <span>Houston, TX · Serving TX / LA / AL / MS</span>
            </div>
          </div>
        </div>

        {/* Right form column */}
        <div className="md:col-span-3">
          {status === "success" ? (
            <div
              className="h-full flex flex-col items-center justify-center text-center bg-emerald-50 border-2 border-emerald-200 rounded-xl p-8"
              data-testid="gc-form-success"
            >
              <CheckCircle2 className="w-12 h-12 text-emerald-600 mb-3" />
              <h3 className="text-2xl font-bold text-emerald-900 mb-2">
                Thanks — we&apos;ve got it.
              </h3>
              <p className="text-emerald-800 max-w-md">
                A senior team member will follow up within one business day. For urgent
                after-hours needs, call{" "}
                <a href="tel:2814563458" className="font-bold underline">281.456.3458</a>.
              </p>
              <button
                type="button"
                onClick={() => setStatus("idle")}
                className="mt-6 px-5 py-2 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold rounded-lg text-sm"
                data-testid="gc-form-send-another"
              >
                Send another
              </button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
              data-testid="gc-start-project-form"
            >
              <div className="sm:col-span-1">
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1">
                  Full Name *
                </label>
                <input
                  required
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder="Jane Doe"
                  data-testid="gc-form-name"
                />
              </div>
              <div className="sm:col-span-1">
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1">
                  Work Email *
                </label>
                <input
                  required
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder="jane@hospital.org"
                  data-testid="gc-form-email"
                />
              </div>
              <div className="sm:col-span-1">
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1">
                  Facility / Company
                </label>
                <input
                  name="company"
                  value={form.company}
                  onChange={onChange}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder="St. Luke's Health"
                  data-testid="gc-form-company"
                />
              </div>
              <div className="sm:col-span-1">
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1">
                  Project Type
                </label>
                <select
                  name="projectType"
                  value={form.projectType}
                  onChange={onChange}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  data-testid="gc-form-project-type"
                >
                  {projectTypes.map((p) => (
                    <option key={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1">
                  Tell us about your project
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={4}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder="Scope, location, timeline, budget range if known..."
                  data-testid="gc-form-message"
                />
              </div>
              {status === "error" && (
                <div
                  className="sm:col-span-2 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800"
                  data-testid="gc-form-error"
                >
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{errMsg}</span>
                </div>
              )}
              <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-3 pt-2">
                <p className="text-[11px] text-slate-500">
                  By submitting, you agree to be contacted about your project.
                </p>
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-700 hover:bg-cyan-800 disabled:bg-slate-400 text-white font-bold rounded-lg text-sm shadow-lg transition-colors"
                  data-testid="gc-form-submit"
                >
                  {status === "sending" ? "Sending…" : "Start My Project"}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

const ServiceCard = ({ icon: Icon, title, description, items, color, image }) => {
  const [expanded, setExpanded] = useState(false);
  const colorMap = {
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', accent: 'text-amber-700', badge: 'bg-amber-100 text-amber-800', iconBg: 'bg-amber-500' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', accent: 'text-blue-700', badge: 'bg-blue-100 text-blue-800', iconBg: 'bg-blue-500' },
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', accent: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-800', iconBg: 'bg-emerald-500' },
    purple: { bg: 'bg-purple-50', border: 'border-purple-200', accent: 'text-purple-700', badge: 'bg-purple-100 text-purple-800', iconBg: 'bg-purple-500' },
  };
  const cs = colorMap[color];
  return (
    <div className={`rounded-xl border ${cs.border} overflow-hidden transition-all duration-300 hover:shadow-lg`} data-testid={`service-card-${color}`}>
      <div className="relative h-48 overflow-hidden">
        <img src={image} alt={title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        <div className={`absolute top-4 left-4 ${cs.iconBg} p-2 rounded-lg`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="absolute bottom-4 left-4 right-4">
          <h3 className="text-xl font-bold text-white">{title}</h3>
        </div>
      </div>
      <div className={`p-5 ${cs.bg}`}>
        <p className="text-sm text-slate-600 mb-4">{description}</p>
        <button onClick={() => setExpanded(!expanded)} className={`flex items-center gap-1 text-sm font-semibold ${cs.accent} mb-3`}>
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          {expanded ? 'Hide Details' : 'View All Services'}
        </button>
        {expanded && (
          <div className="grid grid-cols-2 gap-2 mt-2">
            {items.map((item, i) => (
              <div key={i} className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg ${cs.badge}`}>
                <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ================================================================
// MILESTONE ANNOUNCEMENT — Healthcare Construction Leadership Reveal
// Introduces Donna Winfrey, Kimberly Phipps-Nichols, and Miguel Mendoza.
// Warm intro from George (CEO) + polished bio cards. Editorial layout,
// left-aligned, muted-navy + amber accents to match GC hero.
// ================================================================
const TEAM_PORTRAITS = {
  george:
    "https://customer-assets-cm19k8pv.emergentagent.net/job_831e67a6-d823-415e-9bc6-ce18d6b738ca/artifacts/j83kovjn_WhatsApp%20Image%202025-12-01%20at%2012.45.13_4d75dbdf.jpg",
  donna:
    "https://customer-assets-cm19k8pv.emergentagent.net/job_831e67a6-d823-415e-9bc6-ce18d6b738ca/artifacts/8lpnxz82_donna-winfrey.jpg",
  miguel:
    "https://customer-assets-cm19k8pv.emergentagent.net/job_831e67a6-d823-415e-9bc6-ce18d6b738ca/artifacts/myvkv4o6_Miguel.jpg",
  // Kimberly headshot pending — using elegant monogram fallback
  kimberly:
    "https://customer-assets-cm19k8pv.emergentagent.net/job_831e67a6-d823-415e-9bc6-ce18d6b738ca/artifacts/2ebu47dl_Kimberly.png",
};

const TEAM_MEMBERS = [
  {
    id: "donna-winfrey",
    name: "Donna Winfrey",
    initials: "DW",
    role: "Project & Development Director",
    org: "Sterile Mate LLC · Winfrey Real-Estate Development",
    location: "Baytown, Texas",
    email: "donna.winfrey@sterilemate.com",
    portrait: TEAM_PORTRAITS.donna,
    tags: ["Public-Sector CRE", "Zoning & Entitlement", "Development"],
    highlights: [
      "25 years of commercial real-estate & development experience",
      "7-term Planning & Zoning Commissioner — City of Baytown, TX",
      "2023 President's Lifetime Achievement Award recipient",
      "Leads the Winfrey Real-Estate Development (WRED) partnership",
    ],
    bio: [
      "Donna Winfrey brings a quarter-century of commercial real-estate and development leadership to every Sterile Mate ground-up healthcare project. As Project & Development Director, she is the accountable owner for site selection, entitlement, feasibility, and delivery of ambulatory surgery centers, medical office buildings, and hospital campus expansions — from the raw dirt to the day the first patient walks through the door.",
      "Elected seven consecutive terms as a Planning & Zoning Commissioner for the City of Baytown, Texas, Donna has personally shepherded hundreds of commercial and healthcare-adjacent land-use approvals — giving Sterile Mate an unmatched command of the municipal entitlement process across the Greater Houston MSA and the Gulf Coast.",
      "In 2023 she received the President's Lifetime Achievement Award in recognition of a career defined by public-sector service, minority-owned business development, and healthcare-infrastructure advocacy.",
    ],
    philosophy:
      "\"Every hospital I've helped build is a promise to a community — that when someone's mother, child, or spouse walks through that door frightened, the room they enter was built with the same care I'd want for my own family. That's not a slogan. That's the standard.\"",
    signature_focus: [
      "Ambulatory surgery centers & medical office buildings (MOBs)",
      "Public-sector RFP response, HUB/MBE compliance, entitlement",
      "Site selection, feasibility, and pro-forma modeling",
      "Community engagement and Planning & Zoning navigation",
    ],
    credentials: [
      "Planning & Zoning Commissioner — City of Baytown, TX (7 terms)",
      "President's Lifetime Achievement Award, 2023",
      "25 years commercial real-estate development",
      "MBE / HUB certified vendor of record",
    ],
    stands_for:
      "Public-service-first development. Donna believes healthcare buildings must serve the community first and the balance sheet second — and that minority-owned firms belong at the head of the table on capital projects, not in the sub-tier.",
  },
  {
    id: "kimberly-phipps-nichols",
    name: "Kimberly Phipps-Nichols",
    initials: "KP",
    role: "Principal Architect Consultant",
    org: "Sterile Mate LLC — Healthcare Design",
    location: "Houston, Texas",
    email: null,
    portrait: TEAM_PORTRAITS.kimberly,
    tags: ["EDAC", "LEED AP", "NOMA", "IIDA"],
    highlights: [
      "30+ years of healthcare architectural design experience",
      "Portfolio: Harris Health, Texas Children's, Memorial Hermann",
      "Evidence-Based Design (EDAC) accredited practitioner",
      "Active Rotarian and community civic leader",
    ],
    bio: [
      "Kimberly Phipps-Nichols is a Principal Architect Consultant with more than three decades of healthcare architectural design experience across the Texas Medical Center, Harris Health System, Texas Children's Hospital, and Memorial Hermann — arguably the most demanding healthcare real-estate portfolio in the country.",
      "She holds the Evidence-Based Design Accreditation and Certification (EDAC), Leadership in Energy and Environmental Design Accredited Professional (LEED AP) designations, and is an active member of the National Organization of Minority Architects (NOMA) and the International Interior Design Association (IIDA) — a combination of credentials that positions her at the intersection of clinical outcomes, sustainability, and human-centered healthcare space design.",
      "At Sterile Mate, Kimberly leads the design-review and clinical-planning function on every capital project — reviewing schematic and design-development sets, running mock-up rounds with surgical, sterile-processing, and infection-prevention leadership, and translating operational adjacency needs into buildable, code-compliant architecture that survives the OSHPD / TDLR / TDSHS review cycle.",
    ],
    philosophy:
      "\"Healthcare design is not decoration — it is a clinical intervention. Every surface, every sightline, every door swing either helps a nurse do her job or gets in her way. My job is to make sure the building disappears so the caregivers can appear.\"",
    signature_focus: [
      "Operating room, cath lab, and hybrid OR planning",
      "Sterile processing (SPD) two-tier workflow design",
      "Evidence-based design (EBD) for patient outcomes",
      "Sustainable healthcare (LEED) & indoor environmental quality",
    ],
    credentials: [
      "EDAC — Evidence-Based Design Accredited Practitioner",
      "LEED AP — Sustainable Design",
      "NOMA — National Organization of Minority Architects",
      "IIDA — International Interior Design Association",
      "30+ years healthcare architectural design",
    ],
    stands_for:
      "Human-centered, clinically-informed design. Kimberly stands for buildings that measurably reduce medical error, protect caregiver well-being, and give every patient — regardless of ZIP code — the dignity of a well-designed healing space.",
  },
  {
    id: "miguel-mendoza",
    name: "Miguel Mendoza",
    initials: "MM",
    role: "Principal Owner — MAM Construction Services",
    org: "Sterile Mate GC Partner — Division 9 Lead",
    location: "Houston, Texas",
    email: null,
    portrait: TEAM_PORTRAITS.miguel,
    tags: ["Division 9", "Interior Finishes", "Field Ops"],
    highlights: [
      "12+ years of Division 9 (interior finishes) construction",
      "Turn-key interior build-outs across TX healthcare & commercial",
      "Field superintendent for OR, ASC, and clinic renovations",
      "Fully licensed, insured, and prevailing-wage ready",
    ],
    bio: [
      "Miguel Mendoza is the Principal Owner of MAM Construction Services, Sterile Mate's Division 9 lead partner and the field arm on every healthcare interior build-out. Twelve-plus years in the trades have made him the person you want walking the site the day before turnover — the one who catches the missed penetration seal, the wrong-side door swing, the mis-labeled panel — before the compliance officer does.",
      "MAM Construction Services holds active state licenses and carries full general-liability, workers-comp, and builders-risk insurance — with the paperwork pre-filed in every hospital vendor portal Sterile Mate serves. Miguel personally superintends OR renovations, ASC ground-up interior fit-outs, and clinic refresh programs, and runs a crew that is fluent in ICRA barriers, negative-air setups, and after-hours no-dust protocols.",
      "As a Hispanic-owned business, MAM Construction Services is a key teaming partner in Sterile Mate's MBE + SDVOSB stacking strategy — meaning every project touched by Miguel's team contributes measurable Tier-1 diversity credit to hospital supplier-diversity offices.",
    ],
    philosophy:
      "\"On a hospital floor, there is no such thing as 'good enough.' Either it is right or it is wrong. My crew shows up early, cleans up after themselves, and treats every wall we open as if a patient is behind it — because tomorrow, one will be.\"",
    signature_focus: [
      "Division 9 finishes (flooring, wall protection, ceilings)",
      "OR and ASC interior fit-outs and refresh renovations",
      "ICRA-compliant work in occupied clinical spaces",
      "After-hours and shutdown coordination with clinical ops",
    ],
    credentials: [
      "Principal Owner — MAM Construction Services",
      "Licensed general trades contractor (Texas)",
      "12+ years healthcare & commercial Division 9",
      "Hispanic-owned business — MBE teaming partner",
      "Prevailing-wage compliant crews",
    ],
    stands_for:
      "Craftsmanship as care. Miguel stands for the idea that a well-built room is itself a form of respect — for the patient, for the caregiver, and for the trade. He hires locally, pays fairly, and holds his crew to the standard he'd apply to his own home.",
  },
];

const LeaderCard = ({ member, onOpenBio }) => (
  <article
    className="group relative flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-2xl hover:border-sky-400"
    data-testid={`gc-team-card-${member.id}`}
  >
    {/* Portrait — 4:5 editorial crop */}
    <div className="relative aspect-[4/5] w-full overflow-hidden bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900">
      <img
        src={member.portrait}
        alt={member.name}
        className="h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
        loading="lazy"
      />
      {/* Bottom gradient scrim + name overlay */}
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-slate-950 via-slate-950/70 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-5">
        <h3 className="text-xl font-black leading-tight text-white sm:text-2xl">
          {member.name}
        </h3>
        <p className="mt-1 text-xs font-semibold uppercase tracking-[1.5px] text-sky-300">
          {member.role}
        </p>
      </div>
    </div>

    {/* Body */}
    <div className="flex flex-1 flex-col p-6">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
        {member.org}
      </p>

      {/* Credential chips */}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {member.tags.map((t) => (
          <span
            key={t}
            className="inline-flex items-center rounded-md border border-sky-200 bg-sky-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-sky-800"
          >
            {t}
          </span>
        ))}
      </div>

      {/* Bio bullets */}
      <ul className="mt-4 space-y-2.5">
        {member.highlights.map((h, i) => (
          <li key={i} className="flex items-start gap-2 text-sm leading-snug text-slate-700">
            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-sky-500" />
            <span>{h}</span>
          </li>
        ))}
      </ul>

      {/* Read Full Bio CTA + email */}
      <div className="mt-5 flex flex-col gap-3 border-t border-slate-100 pt-4">
        <button
          type="button"
          onClick={() => onOpenBio(member.id)}
          className="inline-flex items-center justify-between gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-bold text-white transition-all hover:bg-sky-600"
          data-testid={`gc-team-read-bio-${member.id}`}
        >
          <span>Read Full Bio</span>
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </button>
        {member.email && (
          <a
            href={`mailto:${member.email}`}
            className="inline-flex items-center gap-2 text-sm font-semibold text-sky-700 transition-colors hover:text-sky-900"
            data-testid={`gc-team-email-${member.id}`}
          >
            <Mail className="h-4 w-4" />
            {member.email}
          </a>
        )}
      </div>
    </div>
  </article>
);

// Detailed bio modal — opens on "Read Full Bio" click
const LeaderBioDialog = ({ member, open, onOpenChange }) => {
  if (!member) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[92vh] max-w-4xl overflow-y-auto border-0 bg-white p-0 sm:rounded-2xl"
        data-testid={`gc-team-bio-modal-${member.id}`}
      >
        {/* Header — navy portrait strip */}
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-sky-950 p-6 md:p-8">
          <div className="flex flex-col items-start gap-5 sm:flex-row sm:items-center">
            <img
              src={member.portrait}
              alt={member.name}
              className="h-32 w-32 flex-shrink-0 rounded-2xl border-2 border-sky-400/50 object-cover object-top shadow-2xl sm:h-40 sm:w-40"
            />
            <div className="min-w-0 flex-1">
              <DialogHeader className="space-y-1 text-left">
                <p className="text-[10px] font-black uppercase tracking-[2.5px] text-sky-400">
                  Sterile Mate Healthcare Construction · Leadership
                </p>
                <DialogTitle className="text-2xl font-black leading-tight text-white sm:text-3xl md:text-4xl">
                  {member.name}
                </DialogTitle>
                <DialogDescription className="text-sm font-semibold text-sky-300 sm:text-base">
                  {member.role}
                </DialogDescription>
              </DialogHeader>
              <p className="mt-2 text-xs text-slate-400">{member.org}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {member.tags.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center rounded-md border border-sky-400/40 bg-sky-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-sky-300"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="space-y-8 px-6 py-8 md:px-10 md:py-10">
          {/* Bio narrative */}
          <section>
            <p className="mb-3 text-[10px] font-black uppercase tracking-[2.5px] text-sky-600">
              Biography
            </p>
            <div className="space-y-4 text-[15px] leading-relaxed text-slate-700">
              {member.bio.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </section>

          {/* Philosophy pull-quote */}
          <section className="relative rounded-xl border border-sky-100 bg-sky-50/70 px-6 py-6">
            <Quote className="absolute right-4 top-4 h-8 w-8 text-sky-200" />
            <p className="mb-2 text-[10px] font-black uppercase tracking-[2.5px] text-sky-700">
              In Their Own Words
            </p>
            <blockquote className="text-lg font-medium italic leading-relaxed text-slate-800 md:text-xl">
              {member.philosophy}
            </blockquote>
          </section>

          {/* Two-column: Signature focus + Credentials */}
          <section className="grid grid-cols-1 gap-8 md:grid-cols-2">
            <div>
              <p className="mb-3 flex items-center gap-2 text-[10px] font-black uppercase tracking-[2.5px] text-sky-600">
                <Briefcase className="h-3.5 w-3.5" /> Signature Focus
              </p>
              <ul className="space-y-2.5">
                {member.signature_focus.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-sky-500" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="mb-3 flex items-center gap-2 text-[10px] font-black uppercase tracking-[2.5px] text-sky-600">
                <Award className="h-3.5 w-3.5" /> Credentials & Recognition
              </p>
              <ul className="space-y-2.5">
                {member.credentials.map((c, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <BadgeCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-sky-500" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* What they stand for */}
          <section className="rounded-xl border border-slate-200 bg-slate-50 px-6 py-5">
            <p className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-[2.5px] text-sky-600">
              <Heart className="h-3.5 w-3.5" /> What They Stand For
            </p>
            <p className="text-sm leading-relaxed text-slate-700 md:text-base">
              {member.stands_for}
            </p>
          </section>

          {/* Footer meta */}
          <section className="flex flex-col items-start justify-between gap-3 border-t border-slate-200 pt-6 sm:flex-row sm:items-center">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <MapPin className="h-3.5 w-3.5" /> {member.location}
            </div>
            {member.email && (
              <a
                href={`mailto:${member.email}`}
                className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-sky-700"
              >
                <Mail className="h-4 w-4" /> {member.email}
              </a>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const MilestoneAnnouncement = () => {
  const [openMemberId, setOpenMemberId] = useState(null);
  const openMember = TEAM_MEMBERS.find((m) => m.id === openMemberId) || null;

  return (
    <section
      id="gc-meet-the-team"
      className="relative overflow-hidden bg-gradient-to-b from-white via-sky-50/40 to-white py-16 lg:py-24 scroll-mt-24"
      data-testid="gc-milestone-announcement"
    >
      {/* Subtle decorative grid */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(to right, #0f172a 1px, transparent 1px), linear-gradient(to bottom, #0f172a 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-6">
        {/* Header */}
        <div className="mb-12 grid grid-cols-1 items-end gap-8 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-sky-300/60 bg-sky-50 px-3 py-1">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-sky-500" />
              <span className="text-[10px] font-black uppercase tracking-[2.5px] text-sky-700">
                Milestone Announcement · Aug 2026
              </span>
            </div>
            <h2 className="text-3xl font-black leading-[1.05] tracking-tight text-slate-900 md:text-4xl lg:text-5xl">
              Introducing Our{" "}
              <span className="text-sky-600">Healthcare Construction</span>{" "}
              Leadership Team.
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
              50+ years of combined healthcare, real-estate, and Division 9
              construction experience — fully licensed, credentialed, and ready
              to deliver from raw land to first patient.
            </p>
          </div>

          {/* Stat block */}
          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-3">
              {[
                { n: "50+", l: "Combined Years" },
                { n: "100%", l: "Licensed & Bonded" },
              ].map((s) => (
                <div
                  key={s.l}
                  className="rounded-xl border border-sky-100 bg-white p-4 text-center shadow-sm"
                >
                  <p className="text-2xl font-black text-slate-900 md:text-3xl">
                    {s.n}
                  </p>
                  <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-sky-700">
                    {s.l}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* George's warm intro quote card */}
        <div className="mb-14 overflow-hidden rounded-2xl border border-slate-200 bg-slate-950 shadow-xl">
          <div className="grid grid-cols-1 lg:grid-cols-12">
            {/* Signature strip — larger portrait */}
            <div className="relative flex flex-col justify-between bg-gradient-to-br from-slate-900 via-slate-950 to-sky-950 p-8 lg:col-span-5">
              <div>
                <img
                  src={TEAM_PORTRAITS.george}
                  alt="George Broughton III"
                  className="mb-5 h-40 w-40 rounded-full border-4 border-sky-400/70 object-cover object-top shadow-2xl lg:h-44 lg:w-44"
                  loading="lazy"
                />
                <p className="text-[10px] font-black uppercase tracking-[2.5px] text-sky-400">
                  A Note From
                </p>
                <p className="mt-2 text-2xl font-black leading-tight text-white md:text-3xl">
                  George Broughton III
                </p>
                <p className="mt-2 text-xs font-semibold uppercase tracking-widest text-slate-400">
                  Founder &amp; CEO · Sterile Mate LLC
                </p>
              </div>
              <div className="mt-8 hidden lg:block">
                <div className="h-px w-16 bg-sky-400" />
                <p className="mt-3 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                  Chapter Two Begins
                </p>
              </div>
            </div>

            {/* Quote body */}
            <div className="relative p-8 lg:col-span-7 lg:p-10">
              <Quote
                className="absolute right-6 top-6 h-14 w-14 text-sky-400/20"
                aria-hidden="true"
              />
              <blockquote className="relative text-base leading-relaxed text-slate-200 md:text-lg">
                <p>
                  &ldquo;Four years ago, we planted our first seed as an infrastructure
                  partner in the healthcare space with George on an{" "}
                  <span className="font-semibold text-sky-300">
                    AAMI ST108 water-purification system
                  </span>{" "}
                  for critical water. Our next chapter is here.&rdquo;
                </p>
                <p className="mt-4">
                  &ldquo;I am elated to introduce our Healthcare Construction team with
                  more than{" "}
                  <span className="font-semibold text-sky-300">
                    50+ years of combined experience
                  </span>{" "}
                  — fully licensed and credentialed. I am thrilled to empower
                  such a diverse group of leaders who handle each job, each
                  client, each detail with care.&rdquo;
                </p>
              </blockquote>
            </div>
          </div>
        </div>

        {/* Leader cards */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {TEAM_MEMBERS.map((m) => (
            <LeaderCard key={m.id} member={m} onOpenBio={setOpenMemberId} />
          ))}
        </div>

        {/* Footer CTA strip */}
        <div className="mt-12 flex flex-col items-center justify-between gap-4 rounded-2xl border border-sky-100 bg-white px-6 py-5 shadow-sm md:flex-row">
          <div className="flex items-center gap-3">
            <Award className="h-6 w-6 flex-shrink-0 text-sky-600" />
            <p className="text-sm font-semibold text-slate-700 md:text-base">
              Ready to meet the team on your next healthcare build?
            </p>
          </div>
          <a
            href="#gc-start-project"
            className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition-colors hover:bg-sky-700"
            data-testid="gc-team-cta-start-project"
          >
            Start a Project <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>

      {/* Detailed bio modal */}
      <LeaderBioDialog
        member={openMember}
        open={openMemberId !== null}
        onOpenChange={(o) => !o && setOpenMemberId(null)}
      />
    </section>
  );
};



export default function GeneralContractingPage() {
  const [openProjectTile, setOpenProjectTile] = useState(null);
  return (
    <div className="min-h-screen bg-white" data-testid="gc-solutions-page">
      {/* Hero */}
      <div className="relative h-[560px] overflow-hidden bg-slate-950">
        <img src={IMAGES.hero} alt="Healthcare Construction — OR + Clinic" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-900/60 to-slate-900/20" />
        <BackToHome />
        <div className="absolute top-6 right-6 flex items-center gap-3">
          <img src={LOGO_LOCAL} alt="Sterile Mate" className="w-14 h-12 rounded-lg" />
          <div className="hidden md:block">
            <p className="text-white font-bold text-sm">Sterile Mate LLC</p>
            <p className="text-sky-300 text-xs">Keeping You Safe</p>
          </div>
        </div>
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-3xl px-8 md:px-16">
            <div className="flex items-center gap-3 mb-5">
              <img src={LOGO_LOCAL} alt="Sterile Mate" className="w-20 h-16 rounded-xl border border-white/20 object-contain" />
              <div>
                <p className="text-white font-bold text-lg">Sterile Mate LLC</p>
                <p className="text-sky-300 text-xs font-bold tracking-[2.5px] uppercase">Healthcare General Contracting</p>
              </div>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
              Healthcare Construction.{" "}
              <span className="text-sky-300">Delivered at Scale.</span>
            </h1>
            <p className="text-base lg:text-lg text-slate-200 mb-6 max-w-2xl">
              From single-room repairs to $10M+ hospital capital projects — Sterile Mate is a licensed, bonded, ICRA-certified general contractor with in-house architects, permit consultants, and a senior GC team that has delivered healthcare, commercial, and industrial builds across Texas.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href={GC_BROCHURE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 bg-sky-600 hover:bg-sky-700 text-white font-semibold rounded-lg transition-colors shadow-lg"
                data-testid="hero-download-brochure"
              >
                <Download className="w-4 h-4" /> Download Brochure
              </a>
              <a href="mailto:george.broughton@sterilemate.com" className="inline-flex items-center gap-2 px-5 py-3 bg-white/10 backdrop-blur hover:bg-white/20 text-white font-semibold rounded-lg transition-colors border border-white/20">
                <Mail className="w-4 h-4" /> Request a Quote
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Trust bar */}
      <div className="bg-white border-y border-sky-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap justify-center gap-6 md:gap-10">
          {[
            { icon: BadgeCheck, text: 'MBE Certified' },
            { icon: Shield, text: 'ICRA + ILSM Certified' },
            { icon: Award, text: '100% Bonded & Insured' },
            { icon: FileText, text: 'Prevailing Wage Ready' },
            { icon: Users, text: 'In-House Architects' },
            { icon: Clock, text: 'CSP / RFP Response Team' },
            { icon: MapPin, text: 'TX \u00b7 LA \u00b7 AL \u00b7 MS' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-slate-700">
              <item.icon className="w-4 h-4 text-sky-600" />
              <span className="text-sm font-semibold">{item.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ================================================================
          MILESTONE ANNOUNCEMENT — Healthcare Construction Team Reveal
          Warm intro from George + 3 polished leader bio cards
          ================================================================ */}
      <MilestoneAnnouncement />

      {/* Sticky Anchor Nav Rail — jump between sections */}
      <nav
        className="sticky top-0 z-30 bg-slate-950/95 backdrop-blur border-y border-cyan-500/20 shadow-lg"
        aria-label="General Contracting sections"
        data-testid="gc-anchor-nav"
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-center gap-1 sm:gap-4 overflow-x-auto text-white text-xs sm:text-sm font-bold py-3">
          {[
            { href: "#gc-meet-the-team",     label: "Meet the Team" },
            { href: "#gc-project-types",     label: "Project Types" },
            { href: "#gc-wred-partnership",  label: "Partnership + Roadmap" },
            { href: "#gc-capital-projects",  label: "Capital Projects" },
            { href: "#gc-start-project",     label: "Start a Project" },
          ].map((it) => (
            <a
              key={it.href}
              href={it.href}
              className="whitespace-nowrap px-3 py-1.5 rounded-md hover:bg-cyan-500/20 hover:text-cyan-300 transition-colors uppercase tracking-widest"
              data-testid={`gc-anchor-${it.href.slice(4)}`}
            >
              {it.label}
            </a>
          ))}
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6">
        {/* WONDOOR-STYLE PROJECT-TYPE TILE GRID — the primary landing intro */}
        <section id="gc-project-types" className="py-14 lg:py-20 scroll-mt-24" data-testid="gc-project-types">
          <div className="text-center mb-10">
            <p className="text-[11px] font-black uppercase tracking-[2.5px] text-cyan-700 mb-2">
              What We Build
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900 tracking-tight">
              Every Room. Every Wing. Every Building.
            </h2>
            <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              A single-partner construction team for healthcare owners — from a single
              isolation-room retrofit to a ground-up ambulatory surgery center. Bonded,
              insured, prevailing-wage ready, and paired with the Sterile Mate supply
              chain the moment the building opens.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                id: "isolation",
                title: "Isolation & Patient Rooms",
                subtitle: "Airborne · Protective · Bariatric",
                blurb: "Negative-pressure retrofits and full patient-room builds — HEPA-scrubbed anterooms, med-gas headwalls, sound-attenuated wall assemblies, nurse-call rough-in, and infection-control (ICRA/PCRA) barriers throughout construction. Every room delivered turnover-ready with TDLR / TDSHS documentation and pressure-differential balancing reports.",
                img: `${BACKEND_URL}/api/static/gc-tiles/isolation.png`,
              },
              {
                id: "or",
                title: "Operating & Procedure Rooms",
                subtitle: "OR · Cath Lab · Endo · Hybrid",
                blurb: "Turn-key OR builds and refresh work — ceiling-mounted equipment booms, integration racks, laminar-flow HVAC, LED surgical lights, epoxy or welded-seam flooring, and stainless steel or MediRail wall protection. Coordinated with medical-planning teams and equipment vendors for a single hand-off.",
                img: `${BACKEND_URL}/api/static/gc-tiles/or.png`,
              },
              {
                id: "asc",
                title: "Ambulatory Surgery Centers",
                subtitle: "Ground-up ASC & MOB",
                blurb: "Complete ambulatory surgery center and medical office building programs delivered under our Winfrey Real-Estate Development partnership — commercial advisory, site selection, zoning and entitlement, feasibility, design, construction, and licensure. One accountable team from raw land to first patient.",
                img: `${BACKEND_URL}/api/static/gc-tiles/asc.png`,
                highlight: true,
              },
              {
                id: "spd",
                title: "Sterile Processing (SPD)",
                subtitle: "Decontam · Prep-Pack · Sterile Store",
                blurb: "Two-tier SPD suites with proper zone separation, boom-fed pre-wash sinks, ultrasonic pits, floor-mounted washers, sealed pass-throughs, RO-water skids, and clean-side high-shelf sterile storage. Delivered ready for AAMI ST79 workflow validation.",
                img: `${BACKEND_URL}/api/static/gc-tiles/spd.png`,
              },
              {
                id: "pharm",
                title: "Compounding Pharmacies",
                subtitle: "USP 797 / 800 clean rooms",
                blurb: "ISO 7 / ISO 8 clean-room builds — HEPA plenum ceilings, seamless coved flooring, hazardous-drug ante rooms, differential-pressure gauges, integrated pass-throughs, and appropriate exhaust for hazardous compounding. Delivered certification-ready.",
                img: `${BACKEND_URL}/api/static/gc-tiles/pharm.png`,
              },
              {
                id: "water",
                title: "Water & MEP Infrastructure",
                subtitle: "RO / DI · Med Gas · Steam · HVAC",
                blurb: "AAMI-compliant water for sterile processing and dialysis, medical-gas manifolds and alarms, steam and condensate systems, redundant HVAC with proper redundancy sequencing. Design-assist or full design-build across all MEP disciplines.",
                img: `${BACKEND_URL}/api/static/gc-tiles/water.png`,
              },
              {
                id: "behavioral",
                title: "Behavioral Health & NICU",
                subtitle: "Safe rooms · Anti-ligature · NICU",
                blurb: "Ligature-resistant hardware and fixtures, tamper-proof plumbing trim, private psychiatric holding rooms, and specialized NICU / nursery buildouts with proper light and acoustic control. Experienced with the full spectrum of specialty-care code requirements.",
                img: `${BACKEND_URL}/api/static/gc-tiles/behavioral.png`,
              },
              {
                id: "emergency",
                title: "Emergency Break-Fix",
                subtitle: "24/7 skilled trades response",
                blurb: "Licensed electricians, plumbers, and HVAC technicians on standby for hospital after-hours emergencies — union and merit-shop bench with the credentials required to work in an active clinical environment.",
                img: `${BACKEND_URL}/api/static/gc-tiles/emergency.png`,
              },
              {
                id: "renovations",
                title: "Facility Upgrades & Renovations",
                subtitle: "Occupied-facility phased work",
                blurb: "Nights, weekends, and phased-swing renovations inside occupied clinical space. Dust-tight ICRA barriers, temporary partitions, HEPA scrubbers, negative-pressure engineering controls, and zero disruption to patient care.",
                img: `${BACKEND_URL}/api/static/gc-tiles/renovations.png`,
              },
            ].map((t) => {
              const isOpen = openProjectTile === t.id;
              return (
                <div
                  key={t.id}
                  className={`relative overflow-hidden rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 ${t.highlight ? "ring-2 ring-cyan-400 ring-offset-2" : ""}`}
                  data-testid={`gc-tile-${t.id}`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenProjectTile(isOpen ? null : t.id)}
                    aria-expanded={isOpen}
                    aria-controls={`gc-tile-panel-${t.id}`}
                    className="group relative block w-full text-left aspect-[4/3] overflow-hidden"
                    data-testid={`gc-tile-toggle-${t.id}`}
                  >
                    <img
                      src={t.img}
                      alt={t.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-900/60 to-slate-900/20" />
                    {t.highlight && (
                      <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-cyan-400 text-slate-900 font-bold text-[10px] uppercase tracking-widest px-2.5 py-1 shadow-lg">
                        <Star className="w-3 h-3 fill-slate-900" />
                        Featured Capability
                      </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 p-6">
                      <p className="text-[11px] font-bold uppercase tracking-widest text-cyan-300 mb-1">
                        {t.subtitle}
                      </p>
                      <h3 className="text-xl md:text-2xl font-black text-white leading-tight mb-2">
                        {t.title}
                      </h3>
                      <span className="inline-flex items-center gap-1.5 mt-1 text-sm font-bold text-cyan-300 group-hover:text-cyan-200 transition-colors">
                        {isOpen ? "Show Less" : "Learn More"}
                        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                      </span>
                    </div>
                  </button>
                  <div
                    id={`gc-tile-panel-${t.id}`}
                    className={`overflow-hidden transition-[max-height] duration-500 ease-in-out ${isOpen ? "max-h-96" : "max-h-0"}`}
                  >
                    <div className="bg-white p-5 border-t border-slate-100">
                      <p className="text-sm text-slate-700 leading-relaxed">
                        {t.blurb}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="mt-10 text-center text-sm text-slate-500 max-w-2xl mx-auto">
            Every capability above is delivered by our in-house healthcare-construction team
            in partnership with Winfrey Real Estate Development. Tap any card for the full
            scope description.
          </p>
        </section>

        {/* Who We Serve */}
        <section className="py-16">
          <div className="text-center mb-12">
            <h2 className="text-base lg:text-lg font-bold text-amber-600 mb-2">TRUSTED ACROSS PUBLIC &amp; PRIVATE SECTORS</h2>
            <p className="text-3xl font-bold text-slate-900 mb-4">No Job Too Big. No Job Too Small.</p>
            <p className="text-slate-500 max-w-2xl mx-auto">From a single leaking faucet to a $500K capital project — we show up with the same professionalism, compliance, and quality that regulated facilities demand.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {[
              { name: 'Local & State Government', desc: 'Active GC for municipal facility maintenance, public-safety buildings, state agency offices, parks & rec, and water utilities.', type: 'Public Sector' },
              { name: 'Federal Government Contracts', desc: 'Licensed federal contractor for healthcare facility construction, water purification, plumbing, and compliance projects across federal medical campuses.', type: 'Federal Contractor' },
              { name: 'Private Sector Owners', desc: 'Multi-tenant medical office buildings, ambulatory surgery centers, dialysis clinics, dental groups, and private hospital networks.', type: 'Private Sector' },
            ].map((ref, i) => (
              <div key={i} className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-xl p-6 hover:shadow-md transition-shadow" data-testid={`reference-card-${i}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-5 h-5 text-amber-600" />
                  <span className="text-xs font-bold text-amber-600 bg-amber-50 px-2 py-1 rounded">{ref.type}</span>
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{ref.name}</h3>
                <p className="text-sm text-slate-500">{ref.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* WATER PURIFICATION SPECIALTY — Lead capability */}
        <section className="py-12" data-testid="gc-water-specialty">
          <div className="rounded-2xl overflow-hidden border-2 border-cyan-200 shadow-xl">
            {/* Hero strip */}
            <div className="relative h-64 md:h-80">
              <img src={IMAGES.waterSystem} alt="Sterile Mate Healthcare Water Purification System" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-900/90 via-cyan-900/60 to-transparent" />
              <div className="absolute inset-0 flex items-center">
                <div className="px-8 md:px-12 max-w-2xl">
                  <p className="text-cyan-200 text-xs font-bold tracking-widest uppercase mb-2">Core Specialty</p>
                  <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight mb-3">
                    Healthcare Water Purification
                  </h2>
                  <p className="text-white/90 text-sm md:text-base leading-relaxed">
                    Sterile Mate is, first and foremost, a <strong>healthcare water purification specialist</strong>. Our four-discipline GC crew below exists to back every water install — one accountable contractor, one compliance trail.
                  </p>
                </div>
              </div>
            </div>

            {/* Two-column capability split */}
            <div className="bg-white p-6 md:p-10">
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                {/* AAMI ST108 */}
                <div className="border border-cyan-200 rounded-xl overflow-hidden bg-cyan-50/30">
                  <div className="h-44 overflow-hidden">
                    <img src={IMAGES.waterRoom} alt="AAMI ST108 critical water room install" className="w-full h-full object-cover" />
                  </div>
                  <div className="p-5">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-cyan-700 mb-1">AAMI ST108</p>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">Critical Water for SPD</h3>
                    <p className="text-sm text-slate-600 mb-3">
                      Engineered to meet the new AAMI ST108 quality categories for sterile processing — utility through critical water.
                    </p>
                    <ul className="space-y-1.5 text-sm">
                      {[
                        "Reverse Osmosis skids sized for SPD load",
                        "Deionization Tank Exchange Program — scheduled swap, audit-ready logs",
                        "Distribution loops, recirc, sample ports, sterilizer tie-ins",
                      ].map((it, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <Beaker className="w-4 h-4 text-cyan-600 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-800">{it}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Utility Water */}
                <div className="border border-cyan-200 rounded-xl overflow-hidden bg-cyan-50/30">
                  <div className="h-44 overflow-hidden">
                    <img src={IMAGES.utilityWater} alt="Facility-wide utility water treatment and softening" className="w-full h-full object-cover" />
                  </div>
                  <div className="p-5">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-cyan-700 mb-1">Utility Water</p>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">Incoming City Water Treatment</h3>
                    <p className="text-sm text-slate-600 mb-3">
                      Most municipal water arrives loaded with hardness, chloramines, and TDS that destroy equipment over time.
                    </p>
                    <ul className="space-y-1.5 text-sm">
                      {[
                        "Facility-Wide Softening — protect boilers, autoclaves, kitchens, ice machines, cooling towers",
                        "Isolated / Zone Softening — treat just the rooms that need it (SPD, OR, dietary, dialysis)",
                        "Carbon filtration, scale & chloramine removal, pre-treatment for RO",
                      ].map((it, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <Activity className="w-4 h-4 text-cyan-600 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-800">{it}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Standards strip + CTA */}
              <div className="flex flex-wrap items-center justify-between gap-4 bg-cyan-50 border border-cyan-200 rounded-xl p-5">
                <div className="flex flex-wrap gap-4 text-sm">
                  {[
                    { label: "AAMI ST108", desc: "Critical water for sterile processing" },
                    { label: "AAMI RD52 / RD62", desc: "Dialysis water standards" },
                    { label: "Legionella Compliant", desc: "ASHRAE 188 water-mgmt plans" },
                  ].map((std, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <BadgeCheck className="w-4 h-4 text-cyan-700" />
                      <div>
                        <p className="text-xs font-bold text-cyan-900">{std.label}</p>
                        <p className="text-[10px] text-cyan-700">{std.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <Link
                  to="/solutions/water-treatment"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-cyan-700 hover:bg-cyan-800 text-white font-semibold rounded-lg transition-colors shadow"
                  data-testid="gc-water-cta"
                >
                  See Water Purification Solutions <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Core Services */}
        <section className="py-8 pb-16">
          <div className="text-center mb-12">
            <h2 className="text-base lg:text-lg font-bold text-slate-500 mb-2">THE ENHANCEMENT — FOUR-DISCIPLINE GC CREW</h2>
            <p className="text-3xl font-bold text-slate-900">Four Disciplines. One Contractor.</p>
            <p className="text-slate-500 mt-3 max-w-2xl mx-auto text-sm">
              The water specialty is the lead. The GC crew is what makes every install finish on schedule, on permit, and on budget.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <ServiceCard
              icon={Zap}
              title="Electrical Services"
              description="Our licensed electricians specialize in healthcare-grade electrical systems — panel upgrades, medical equipment wiring, emergency power, and code-compliant installations."
              image={IMAGES.electrical}
              color="amber"
              items={[
                'Electrical Panel Upgrades', 'Medical Equipment Wiring', 'Emergency Power Systems',
                'Lighting & Controls', 'Code Compliance Retrofits', 'Circuit Install & Repair',
                'Generator Connections', 'Fire Alarm Systems', 'Low-Voltage & Data', 'Arc Flash Studies'
              ]}
            />
            <ServiceCard
              icon={Droplets}
              title="Plumbing Services"
              description="Master plumbers building and maintaining critical healthcare systems — sterile processing water, gas piping, medical gas, and backflow prevention."
              image={IMAGES.plumbing}
              color="blue"
              items={[
                'Healthcare Water Systems', 'Medical Gas Piping', 'Leak Detection & Repair',
                'Gas Line Installation', 'Backflow Prevention', 'Drain & Sewer Systems',
                'Water Heater Systems', 'Fixture Install/Replace', 'Boiler & Chiller Support', 'Code Retrofits'
              ]}
            />
            <ServiceCard
              icon={Hammer}
              title="Construction & Buildout"
              description="Full room buildouts, patient area remodels, new construction, and facility expansions. Framing, drywall, flooring, medical-grade finishes."
              image={IMAGES.construction}
              color="emerald"
              items={[
                'Room Buildouts', 'Patient Area Remodels', 'New Construction', 'Drywall & Framing',
                'Medical-Grade Flooring', 'Painting & Finishes', 'Ceiling Systems', 'ADA Compliance',
                'Demolition', 'Tenant Improvements'
              ]}
            />
            <ServiceCard
              icon={Wrench}
              title="Repairs & Maintenance"
              description="Hospitals never close — and neither do we. Emergency leaks, gas repairs, electrical outages, and urgent facility issues handled with compliance and speed."
              image={IMAGES.hospital}
              color="purple"
              items={[
                'Emergency Leak Repair', 'Gas Line Repairs', 'Electrical Troubleshooting',
                'HVAC Support', 'Fixture Replacement', 'Preventive Maintenance',
                'After-Hours Service', 'Priority Response', 'Drywall Patching', 'General Handyman'
              ]}
            />
          </div>
        </section>

        {/* Compliance Section */}
        <section className="py-16 -mx-6 px-6 bg-slate-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-base lg:text-lg font-bold text-red-600 mb-2">HEALTHCARE COMPLIANCE</h2>
                <p className="text-3xl font-bold text-slate-900 mb-6">Why Standard Contractors Fail in Healthcare</p>
                <p className="text-slate-600 mb-6">Working in a hospital is fundamentally different from working in a commercial building. Patient safety, infection control, air quality, noise restrictions, and regulatory compliance create a set of requirements that most general contractors simply don&apos;t understand.</p>
                <div className="space-y-3">
                  {[
                    { label: 'ICRA Compliant', desc: 'Infection Control Risk Assessment — all classes (I-IV)' },
                    { label: 'ILSM Certified', desc: 'Interim Life Safety Measures always in place' },
                    { label: 'Full Background Checks', desc: 'Healthcare-level vetting, badging, and clearance' },
                    { label: 'OSHA Healthcare', desc: 'Healthcare-specific safety standards, not just general OSHA' },
                    { label: 'Joint Commission Ready', desc: 'Documentation and processes built for audit readiness' },
                    { label: 'Infection Control Barriers', desc: 'HEPA filtration + negative pressure containment' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 bg-white rounded-lg p-3 border border-slate-200">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-semibold text-sm text-slate-900">{item.label}</span>
                        <p className="text-xs text-slate-500">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-4">
                <img src={IMAGES.hallway} alt="Healthcare Facility" className="w-full h-64 object-cover rounded-xl shadow-lg" />
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <span className="font-bold text-amber-800">Did You Know?</span>
                  </div>
                  <p className="text-sm text-amber-700 leading-relaxed">
                    A standard contractor entering a hospital without ICRA compliance can trigger a Joint Commission citation, shut down a patient area, or compromise infection control — potentially costing a health system millions in fines, remediation, and patient harm.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ================================================================ */}
        {/* Healthcare Construction Leadership Team                          */}
        {/* ================================================================ */}
        <section className="py-16" data-testid="gc-leadership-team">
          <div className="text-center mb-12">
            <h2 className="text-base lg:text-lg font-bold text-amber-600 mb-2">HEALTHCARE INFRASTRUCTURE LEADERSHIP</h2>
            <p className="text-3xl font-bold text-slate-900 mb-4">A Senior Team Purpose-Built for Hospital Construction</p>
            <p className="text-slate-500 max-w-3xl mx-auto">
              Sterile Mate&apos;s Healthcare Infrastructure division is led by senior professionals whose combined experience spans commercial real estate advisory, municipal zoning, hospital construction, industrial builds, and permit expediting. Every large project is driven end-to-end by this in-house team — not handed off to strangers.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Briefcase,
                role: 'Project Manager & Owner\u2019s Representative',
                title: 'Commercial Property Advisor',
                desc: 'Zoning Chair on a Gulf Coast municipality with authority-having-jurisdiction relationships across the region. Sits at the intersection of ownership representation, municipal approvals, and healthcare facility development. Runs each capital project as single point of accountability.',
                color: 'from-amber-50 to-white border-amber-200',
                iconBg: 'bg-amber-500',
              },
              {
                icon: HardHat,
                role: 'Senior General Contractors',
                title: 'Multi-Discipline GC Leadership',
                desc: 'Veteran general contractors with delivered portfolios spanning commercial healthcare (hospitals, ambulatory surgery centers, dialysis clinics), industrial (petro-chem, water treatment, food processing), and institutional builds. Full self-perform crews plus vetted subcontractor bench.',
                color: 'from-slate-50 to-white border-slate-200',
                iconBg: 'bg-slate-700',
              },
              {
                icon: Building2,
                role: 'Architects On Team',
                title: 'Licensed Architects & Designers',
                desc: 'In-house and partner-firm architects for healthcare programming, TDLR / TDSHS / AHJ code review, life-safety plan production, and coordination with medical planners. Design-build ready when the health system needs a single-source contract.',
                color: 'from-cyan-50 to-white border-cyan-200',
                iconBg: 'bg-cyan-600',
              },
              {
                icon: ClipboardCheck,
                role: 'Permit & Approvals',
                title: 'Permit Consultants & Expeditors',
                desc: 'Dedicated permit consultants managing local, state, and federal approvals — city building, MEP, health department, fire marshal, TDLR, and TDSHS. Familiar with Harris Health, HCA, Memorial Hermann, and VA facility submission workflows.',
                color: 'from-emerald-50 to-white border-emerald-200',
                iconBg: 'bg-emerald-600',
              },
            ].map((person, i) => (
              <div
                key={i}
                className={`bg-gradient-to-br ${person.color} border rounded-xl p-6 hover:shadow-lg transition-shadow flex flex-col`}
                data-testid={`leadership-role-${i}`}
              >
                <div className={`w-12 h-12 ${person.iconBg} rounded-lg flex items-center justify-center mb-4`}>
                  <person.icon className="w-6 h-6 text-white" />
                </div>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">{person.role}</p>
                <h3 className="font-bold text-slate-900 text-lg mb-3">{person.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{person.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 bg-slate-900 rounded-xl p-8 text-white">
            <div className="grid md:grid-cols-3 gap-8 items-center">
              <div className="md:col-span-2">
                <h3 className="text-2xl font-bold mb-3">One Team. One Contract. One Point of Accountability.</h3>
                <p className="text-slate-300 leading-relaxed">
                  When a health system engages Sterile Mate for a capital project, they get a single-source integrated team: the property advisor / project manager, senior GCs, licensed architects, and permit consultants — all working from the same schedule, the same budget, and the same set of drawings. No finger-pointing between disciplines. No dropped baton between design and construction.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3 text-center">
                <div className="bg-white/10 rounded-lg p-4">
                  <p className="text-3xl font-bold text-amber-400">50+</p>
                  <p className="text-xs text-slate-300 mt-1">Years combined healthcare & industrial construction experience across the team</p>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <p className="text-3xl font-bold text-amber-400">$50M+</p>
                  <p className="text-xs text-slate-300 mt-1">Aggregate capital project value delivered by team members</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ================================================================ */}
        {/* NEW: Winfrey Development Partnership + Infrastructure Roadmap    */}
        {/* ================================================================ */}
        <section id="gc-wred-partnership" className="py-16 scroll-mt-24" data-testid="gc-wred-partnership">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 text-amber-900 font-bold text-[11px] uppercase tracking-widest px-3 py-1.5 mb-4">
              <Building2 className="w-3.5 h-3.5" />
              New Partnership · Sterile Mate + Winfrey Development
            </div>
            <h2 className="text-base lg:text-lg font-bold text-amber-600 mb-2">
              INFRASTRUCTURE PARTNER — CONCEPT THROUGH COMMISSIONING
            </h2>
            <p className="text-3xl font-bold text-slate-900 mb-4">
              From Zoning Chair to Ribbon Cutting — One Roadmap, One Team
            </p>
            <p className="text-slate-500 max-w-3xl mx-auto leading-relaxed">
              Sterile Mate has partnered with <strong>Winfrey Real Estate Development (WRED)</strong>,
              a women-owned, seasoned commercial developer led by an active zoning-board
              official. WRED expands Sterile Mate&apos;s scope beyond construction into
              <strong> commercial advisory, zoning, feasibility, and construction oversight</strong>
              — so healthcare systems get a single partner for every phase of expansion. Whether
              you&apos;re building a critical water system, a compliant clean room, an entire
              ambulatory surgery center, or a new healthcare clinic, Sterile Mate is there
              from first site walk to first patient.
            </p>
          </div>

          {/* Welcome Donna spotlight card */}
          <div className="mb-12 grid md:grid-cols-3 gap-6 items-stretch">
            <div className="md:col-span-2 bg-gradient-to-br from-amber-50 via-white to-cyan-50 border-2 border-amber-200 rounded-2xl p-8 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center text-white font-black text-lg">
                  DW
                </div>
                <div>
                  <p className="text-xs font-bold text-amber-700 uppercase tracking-widest">Welcome to the Team</p>
                  <h3 className="text-xl font-bold text-slate-900">Donna Winfrey — Winfrey Real Estate Development</h3>
                </div>
              </div>
              <p className="text-slate-700 leading-relaxed mb-4">
                Donna is a veteran commercial developer, an active <strong>zoning-board
                leader in a Gulf-Coast Texas municipality</strong>, and the founder of the
                women-owned firm Winfrey Real Estate Development. She brings a proven team
                of licensed architects, general contractors, and civil engineers with deep
                healthcare-facility experience.
              </p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-start gap-2">
                  <ClipboardCheck className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <span className="text-slate-700">Municipal zoning &amp; entitlement authority</span>
                </div>
                <div className="flex items-start gap-2">
                  <Building2 className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <span className="text-slate-700">Active healthcare-facility development</span>
                </div>
                <div className="flex items-start gap-2">
                  <HardHat className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <span className="text-slate-700">Architects, builders, civil engineers on team</span>
                </div>
                <div className="flex items-start gap-2">
                  <Briefcase className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <span className="text-slate-700">Women-owned business — WBE / HUB eligible</span>
                </div>
              </div>
              <p className="text-xs text-slate-500 italic mt-4">
                Past performance and reference letters available upon request during the bid process.
              </p>
            </div>

            {/* Right rail: expanded capability list */}
            <div className="bg-slate-900 rounded-2xl p-6 text-white shadow-lg">
              <p className="text-xs font-bold text-cyan-300 uppercase tracking-widest mb-3">
                Scope Expansion With WRED
              </p>
              <ul className="space-y-2 text-sm">
                {[
                  "Commercial real-estate advisory",
                  "Site selection &amp; acquisition",
                  "Zoning, variance &amp; entitlement",
                  "Feasibility &amp; pro-forma modeling",
                  "Municipal approval expediting",
                  "Owner&apos;s-rep construction oversight",
                  "Post-occupancy supply chain hand-off",
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" />
                    <span className="text-slate-200" dangerouslySetInnerHTML={{ __html: t }} />
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* 7-Phase Roadmap */}
          <div>
            <h3 className="text-center text-xl font-bold text-slate-900 mb-2">
              The Sterile Mate Healthcare Infrastructure Roadmap
            </h3>
            <p className="text-center text-slate-500 text-sm mb-8">
              Every phase — from raw land or existing shell to open doors and full supply
              chain — under one accountable partnership.
            </p>

            <div className="relative">
              {/* Progress line (desktop only) */}
              <div className="hidden lg:block absolute top-8 left-[6%] right-[6%] h-[3px] bg-gradient-to-r from-cyan-500 via-amber-400 to-emerald-500 rounded-full" />

              <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4 relative">
                {[
                  { n: "01", t: "Advisory &amp; Feasibility",   d: "Site walk, pro-forma, needs assessment, healthcare-market study — Donna &amp; WRED lead", tone: "cyan" },
                  { n: "02", t: "Zoning &amp; Entitlement",     d: "Variances, municipal approvals, board hearings — inside track via zoning-chair experience", tone: "cyan" },
                  { n: "03", t: "Design &amp; Programming",     d: "Licensed architects, medical planning, TDLR / TDSHS / AHJ code review, life-safety plans", tone: "amber" },
                  { n: "04", t: "Preconstruction",              d: "GMP budgeting, CD-set validation, permit expediting, MEP + life-safety coordination", tone: "amber" },
                  { n: "05", t: "Construction",                 d: "Self-perform crews, vetted subs, ICRA/PCRA infection-control on active hospital sites", tone: "amber" },
                  { n: "06", t: "Commissioning &amp; TDLR",     d: "USP 797/800, RO water, medical-gas certifications, TDLR inspections, punch &amp; turnover", tone: "emerald" },
                  { n: "07", t: "Operate &amp; Supply",         d: "Post-opening supply chain: PPE, EVS, sterile processing, backorder sourcing — for life", tone: "emerald" },
                ].map((s) => (
                  <li key={s.n} className="relative bg-white border-2 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow"
                      style={{ borderColor: s.tone === "cyan" ? "#67e8f9" : s.tone === "amber" ? "#fcd34d" : "#6ee7b7" }}>
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-black text-sm mb-3 mx-auto ${s.tone === "cyan" ? "bg-cyan-600" : s.tone === "amber" ? "bg-amber-500" : "bg-emerald-600"}`}>
                      {s.n}
                    </div>
                    <p className="text-sm font-bold text-slate-900 text-center mb-1" dangerouslySetInnerHTML={{ __html: s.t }} />
                    <p className="text-xs text-slate-600 leading-snug text-center" dangerouslySetInnerHTML={{ __html: s.d }} />
                  </li>
                ))}
              </ol>
            </div>

            <div className="mt-10 text-center text-sm text-slate-500 max-w-2xl mx-auto">
              Past-performance references, licenses, bonding letters, and a full
              capabilities package are available upon request during the bid process.
            </div>
          </div>
        </section>

        {/* ================================================================ */}
        {/* Large-Scale Capital Project Capabilities                         */}
        {/* ================================================================ */}
        <section id="gc-capital-projects" className="py-16 -mx-6 px-6 bg-gradient-to-br from-slate-900 to-slate-800 text-white scroll-mt-24" data-testid="gc-capital-projects">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-base lg:text-lg font-bold text-amber-400 mb-2">LARGE-SCALE HEALTHCARE CAPITAL PROJECTS</h2>
              <p className="text-3xl font-bold mb-4">Built to Bid, Bond, and Deliver Hospital-Scale Projects</p>
              <p className="text-slate-300 max-w-3xl mx-auto">
                Whether it&apos;s an isolation-room build-out, a nursery construction, a radiation oncology suite renovation, or a full LINAC replacement — Sterile Mate is bonded, prevailing-wage-ready, and CSP-response-capable for public and private health system capital work.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {[
                {
                  icon: DollarSign,
                  title: '100% Bonding Capacity',
                  desc: 'Performance and payment bonds available for the full construction value on any healthcare or public capital project.',
                },
                {
                  icon: Gavel,
                  title: 'Prevailing Wage Compliance',
                  desc: 'Texas GC \u00a7 2258 & federal Davis-Bacon ready. Certified payroll submissions (WH-347), fringe benefit accounting, and wage-rate audits standard on every public project.',
                },
                {
                  icon: FileText,
                  title: 'CSP / RFP Response Team',
                  desc: 'Full Competitive Sealed Proposal response infrastructure — Harris Health, VA, HCA, city, county, and state formats. Proposal, methodology, safety history, financial statement, and MWBE plan documents kept RFP-ready.',
                },
                {
                  icon: Award,
                  title: 'MWBE / HUB Partnership',
                  desc: 'Sterile Mate is HUB / MBE certified and routinely partners on the 22%+ MWBE utilization goals demanded by Harris Health, Metro Houston, TxDOT, and federal health system procurements.',
                },
                {
                  icon: Shield,
                  title: 'ICRA & ILSM at Scale',
                  desc: 'Class III/IV containment for occupied-hospital work. Negative pressure enclosures, HEPA scrubbers, dedicated pathways, sequenced shutdowns — full documentation package for Joint Commission audit trail.',
                },
                {
                  icon: ClipboardCheck,
                  title: 'QA/QC + Monthly Reporting',
                  desc: 'Formal Quality Assurance / Quality Control program with material verification, workmanship inspection, deficiency correction plans, monthly executive dashboards, and photo-documented as-builts at closeout.',
                },
              ].map((cap, i) => (
                <div
                  key={i}
                  className="bg-white/5 border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-colors"
                  data-testid={`capital-capability-${i}`}
                >
                  <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                    <cap.icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{cap.title}</h3>
                  <p className="text-sm text-slate-300 leading-relaxed">{cap.desc}</p>
                </div>
              ))}
            </div>

            {/* Representative Project Types */}
            <div className="bg-black/30 border border-white/10 rounded-xl p-8">
              <p className="text-xs font-bold text-amber-400 uppercase tracking-wide mb-3">Representative Project Types We Bid & Deliver</p>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { title: 'Nursery & NICU Buildouts', ex: 'Isolation rooms, negative-pressure suites, med-gas systems, family visitation spaces.' },
                  { title: 'Radiation Oncology Suites', ex: 'LINAC vault construction, lead-lined shielding, HDR/brachy rooms, simulator suite renovation.' },
                  { title: 'Sterile Processing Departments', ex: 'AAMI ST108-aligned SPD construction, decontam / clean / sterile flow, water-quality integration.' },
                  { title: 'ORs & Ambulatory Surgery Suites', ex: 'Positive-pressure ORs, laminar-flow HVAC, medical gas certification, endoscopy suite fit-outs.' },
                  { title: 'Hospital Water Systems', ex: 'Full RO/DI/EDI plant construction, sterile-processing water loops, legionella mitigation.' },
                  { title: 'Emergency Department Expansions', ex: 'Fast-track occupied-hospital ED renovations with ICRA phasing.' },
                  { title: 'Isolation Cohort Wings', ex: 'Airborne infection isolation rooms (AIIR), anteroom construction, HVAC pressure balancing.' },
                  { title: 'Industrial & Facility Support', ex: 'Chemical storage, mechanical room upgrades, generator + UPS plants, docks & loading facilities.' },
                ].map((proj, i) => (
                  <div key={i} className="border-l-2 border-amber-500 pl-4">
                    <p className="font-bold text-white text-sm mb-1">{proj.title}</p>
                    <p className="text-xs text-slate-400 leading-relaxed">{proj.ex}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Project Gallery */}
        <section className="py-16">
          <div className="text-center mb-12">
            <h2 className="text-base lg:text-lg font-bold text-slate-500 mb-2">PROJECT TYPES</h2>
            <p className="text-3xl font-bold text-slate-900">From Emergency Repairs to Capital Projects</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {[
              { img: IMAGES.buildout, label: 'Capital Projects', desc: 'Full-scale healthcare construction' },
              { img: IMAGES.construction, label: 'Room Buildouts', desc: 'Patient rooms, labs, OR suites' },
              { img: IMAGES.hallway, label: 'Remodels', desc: 'Upgrades & code compliance' },
              { img: IMAGES.hospital, label: 'Facility Maintenance', desc: '24/7 response capability' },
            ].map((proj, i) => (
              <div key={i} className="group relative rounded-xl overflow-hidden h-56 cursor-pointer" data-testid={`project-tile-${i}`}>
                <img src={proj.img} alt={proj.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="text-white font-bold text-sm">{proj.label}</p>
                  <p className="text-white/70 text-xs">{proj.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* How We Work */}
        <section className="py-16 -mx-6 px-6 bg-slate-900 text-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-base lg:text-lg font-bold text-amber-400 mb-2">OUR PROCESS</h2>
              <p className="text-3xl font-bold">Built for Healthcare — From Estimate to Closeout</p>
            </div>
            <div className="grid md:grid-cols-5 gap-6">
              {[
                { n: '01', t: 'Site Assessment', d: 'Evaluate the work area, identify ICRA classifications, document existing conditions.' },
                { n: '02', t: 'Detailed Scope', d: 'Transparent proposals with clear line items. No hidden charges. Materials, labor, timeline.' },
                { n: '03', t: 'Compliant Execution', d: 'Vetted, badged technicians with infection control barriers and noise management.' },
                { n: '04', t: 'Quality Inspection', d: 'Every circuit tested, every joint pressure-checked, every surface inspected.' },
                { n: '05', t: 'Audit-Ready Closeout', d: 'As-built drawings, certificates, compliance checklists, photo documentation.' },
              ].map((step, i) => (
                <div key={i} className="text-center">
                  <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center mx-auto mb-4">
                    <span className="text-lg font-bold text-white">{step.n}</span>
                  </div>
                  <h4 className="font-bold text-white mb-2">{step.t}</h4>
                  <p className="text-sm text-slate-400">{step.d}</p>
                </div>
              ))}
            </div>

            {/* AskIsaac Section */}
            <div className="mt-16 bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <img src={LOGO_LOCAL} alt="Sterile Mate" className="w-12 h-10 rounded-lg object-contain" />
                    <div>
                      <p className="text-amber-400 text-xs font-bold tracking-widest">POWERED BY</p>
                      <p className="text-white text-xl font-bold">AskIsaac — Intelligent Dispatch</p>
                    </div>
                  </div>
                  <p className="text-slate-300 text-sm leading-relaxed mb-6">
                    Every Sterile Mate contracting job is coordinated through <span className="text-amber-400 font-semibold">AskIsaac</span>, our AI-powered intelligent dispatch and operations platform. From the moment a service request comes in to final closeout documentation, AskIsaac ensures the right technician, the right compliance protocol, and the right materials are dispatched — every time.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { t: 'Instant Dispatch', d: 'AI routes the right licensed technician to the right job' },
                      { t: 'Live Tracking', d: 'Real-time status updates for facility managers' },
                      { t: 'Compliance Docs', d: 'Auto-generated audit-ready documentation' },
                      { t: 'Telegram-First', d: 'Text-based requests — no app download needed' },
                    ].map((feat, i) => (
                      <div key={i} className="bg-white/5 rounded-lg p-3 border border-white/10">
                        <p className="text-amber-400 text-xs font-bold mb-1">{feat.t}</p>
                        <p className="text-slate-400 text-xs">{feat.d}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Start a Project — proper lead capture form (posts to Mongo via /api/leads/coming-soon) */}
        <StartProjectForm />

        {/* CTA */}
        <section className="py-16">
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-amber-900 rounded-2xl p-10 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to Discuss Your Project?</h2>
            <p className="text-slate-300 max-w-2xl mx-auto mb-8">Whether it&apos;s a capital project, emergency repair, or facility remodel — we have the licenses, experience, and healthcare compliance to get it done right.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href={GC_BROCHURE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-colors"
                data-testid="cta-download-brochure"
              >
                <FileText className="w-5 h-5" /> Download Brochure
              </a>
              <a href="mailto:george.broughton@sterilemate.com" className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-colors border border-white/20">
                <Mail className="w-5 h-5" /> george.broughton@sterilemate.com
              </a>
              <a href="tel:2814563458" className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-colors border border-white/20">
                <Phone className="w-5 h-5" /> 281.456.3458
              </a>
              <a href="tel:3232439139" className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-colors border border-white/20">
                <Phone className="w-5 h-5" /> 323.243.9139
              </a>
            </div>
            <div className="flex justify-center gap-6 mt-4">
              <a href="https://www.sterilemate.com" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 text-sm font-medium">www.sterilemate.com</a>
              <a href="https://sterilemate.com" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:text-sky-300 text-sm font-medium">AskIsaac.io</a>
            </div>
            <p className="text-slate-400 text-sm mt-4">HUB Certified | Licensed | Healthcare Compliant | Houston, TX</p>
          </div>
        </section>
      </div>
    </div>
  );
}
